import { Section } from '~/components/layout';
import { investShowcase, type Instrument } from '~/content/invest';
import { ScrollStrip } from './ScrollStrip';

const ROWS = 3;

/** Split the instrument list into N roughly-even marquee rows. */
function toRows(items: Instrument[], rows: number): Instrument[][] {
  const perRow = Math.ceil(items.length / rows);
  return Array.from({ length: rows }, (_, r) => items.slice(r * perRow, (r + 1) * perRow));
}

/** Rounded 104×128 tile: centered logo (monogram fallback), name (truncated), ticker. */
function InstrumentChip({ instrument }: { instrument: Instrument }) {
  return (
    <div className="mx-2.5 flex h-[128px] w-[104px] flex-col items-center justify-center rounded-2xl bg-grey-25 px-3 text-center">
      {instrument.logo ? (
        <img
          src={instrument.logo}
          alt=""
          aria-hidden
          loading="lazy"
          className="size-10 shrink-0 object-contain"
        />
      ) : (
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-grey-100 text-body-s font-semibold text-ink">
          {instrument.symbol.slice(0, 2)}
        </span>
      )}
      <span className="mt-3 w-full">
        <span className="block w-full truncate text-body-s font-semibold text-ink">
          {instrument.name}
        </span>
        {/* 12px (mt-3) gap between the company name and its ticker symbol. */}
        <span className="mt-3 block text-[12px] text-grey-600">{instrument.symbol}</span>
      </span>
    </div>
  );
}

/**
 * "5000+ Stocks, ETFs & more" showcase — the instrument list rendered as three
 * seamless marquee rows, reusing {@link ScrollStrip}'s `autoScroll` capability
 * (drag pauses, resumes when idle, respects reduced-motion). Rows alternate
 * drift direction: ltr / rtl / ltr.
 */
export function InvestShowcase() {
  // TODO(cms): stocks list + logos will come from CMS
  const rows = toRows(investShowcase.instruments, ROWS);

  return (
    <Section className="mt-[120px]">
      <div className="text-center">
        <h2 className="text-h4 font-semibold text-balance">{investShowcase.title}</h2>
        <p className="mx-auto mt-6 max-w-[460px] text-body text-grey-900 whitespace-normal md:whitespace-pre-line">
          {investShowcase.subtitle}
        </p>
      </div>

      <div className="mt-[72px] flex flex-col gap-5">
        {rows.map((row, i) => (
          <ScrollStrip
            key={i}
            plain
            autoScroll
            direction={i === 1 ? 'rtl' : 'ltr'}
            items={row}
            getKey={(item) => item.symbol}
            renderItem={(item) => <InstrumentChip instrument={item} />}
          />
        ))}
      </div>
    </Section>
  );
}
